import { z } from 'zod';
import { rcrmCustomFieldEntrySchema, rcrmListSchema, rcrmTimestampSchema, rcrmUserRefSchema, } from './common.js';
/**
 * RecruitCRM records as `GET /v1/{entity}/{slug}` answers them (bare objects, never wrapped).
 * Only `id` and `slug` are required; the field lists name what the CRM maps or Relay reads, so a
 * consumer gets types for them — RCRM sends many more, and they pass through. Field evidence:
 * `Relay/.test/recent_jobs.json` (jobs, full), `Relay/.test/note.json` (a note with its job and
 * company expanded), `Relay/apps/recruitment-portal/src/lib/rcrm-sync-core.ts` (candidates),
 * `Relay/supabase/functions/recruitcrm-webhook/index.ts` (contacts), the official docs for the rest.
 */
const str = z.string().nullish();
const num = z.number().nullish();
/** A count or id RCRM may send as a number or a numeric string. */
const numberish = z.union([z.number(), z.string()]).nullish();
const customFields = z.array(rcrmCustomFieldEntrySchema).optional();
const audit = {
    created_on: rcrmTimestampSchema.optional(),
    updated_on: rcrmTimestampSchema.optional(),
    created_by: rcrmUserRefSchema.nullish(),
    /** Always the API-token owner for a write made through the API — it cannot tell callers apart. */
    updated_by: rcrmUserRefSchema.nullish(),
    owner: rcrmUserRefSchema.nullish(),
    resource_url: z.string().optional(),
};
export const rcrmCandidateSchema = z.looseObject({
    id: z.number(),
    slug: z.string(),
    first_name: str,
    last_name: str,
    email: str,
    contact_number: str,
    /** 1 male, 2 female, 3 other (Relay `stage-write.ts`); 0 clears. */
    gender_id: numberish,
    contact_gender: str,
    position: str,
    current_organization: str,
    work_ex_year: numberish,
    locality: str,
    city: str,
    state: str,
    country: str,
    source: str,
    /** `{ file_link, filename }`, or a bare link on older records. */
    resume: z.union([z.looseObject({ file_link: str, filename: str }), z.string()]).nullish(),
    avatar: str,
    candidate_summary: str,
    custom_fields: customFields,
    ...audit,
});
export const rcrmContactSchema = z.looseObject({
    id: z.number(),
    slug: z.string(),
    first_name: str,
    last_name: str,
    email: str,
    contact_number: str,
    designation: str,
    company_slug: str,
    additional_company_slugs: z.unknown().optional(),
    is_email_opted_out: z.unknown().optional(),
    contact_stage_remarks: z.unknown().optional(),
    custom_fields: customFields,
    ...audit,
});
export const rcrmCompanySchema = z.looseObject({
    id: z.number(),
    slug: z.string(),
    company_name: str,
    website: str,
    city: str,
    address: str,
    locality: str,
    state: str,
    country: str,
    /** The company's contacts, as slugs (an array on `note.json`'s expanded company). */
    contact_slug: z.union([z.array(z.string()), z.string()]).nullish(),
    custom_fields: customFields,
    ...audit,
});
/**
 * `job_status` reads as `{ id, label }` and is written as the bare id. 🔴 Id 0 is "Closed", a
 * real status: never test it for truthiness.
 */
export const rcrmJobStatusSchema = z.looseObject({ id: z.number(), label: z.string().nullish() });
export const rcrmJobSchema = z.looseObject({
    id: z.number(),
    slug: z.string(),
    name: str,
    company_slug: str,
    /** The primary contact: a FLAT slug on the raw endpoint (Relay `direct-client.ts` `jobContactSlug`). */
    contact_slug: str,
    secondary_contact_slugs: z.unknown().optional(),
    number_of_openings: numberish,
    job_status: z.union([rcrmJobStatusSchema, z.number()]).nullish(),
    job_description_text: str,
    /** `"Full Time"` strings on recorded payloads; the docs say an enum 1–4. Not synced. */
    job_type: z.unknown().optional(),
    salary_type: z.unknown().optional(),
    enable_job_application_form: numberish,
    application_form_url: str,
    /** 0 = the master hiring pipeline. A job carries no pipeline stages (Relay nitpick 2026-10-12). */
    hiring_pipeline_id: num,
    custom_fields: customFields,
    ...audit,
});
/**
 * A deal stage as `GET /v1/deals-pipeline` lists it ("Get Deals Stages",
 * https://docs.recruitcrm.io/docs/rcrm-api-reference/cb4223f869e95-get-deals-stages) and as a
 * deal's `deal_stage` reads (Relay `packages/integrations/src/recruitcrm/entities/deals.ts`,
 * RELAY-322). `percentage` is a string in the docs. 🔴 Never test an id for truthiness.
 */
export const rcrmDealStageSchema = z.looseObject({
    id: z.number(),
    label: z.string().nullish(),
    percentage: numberish,
});
/**
 * A deal (`GET /v1/deals/{slug}`, "Find deal by slug"). `deal_stage` is documented as a string but
 * Relay reads it as the nested `{ id, label, percentage }` (RELAY-322), confirmed by a production
 * read on 2026-10-31 — observed wins, both pass.
 * The CRM only writes `deal_stage` (TS-1878); the rest is listed for the simulator and for reading.
 * The same production write showed two more fields the docs get wrong: `deal_type` is an object
 * (`{ id: 1, label: "New Business" }`, documented as a number) and `contact_slugs` an ARRAY of
 * slugs (documented as a comma-separated string), as are `additional_company_slugs` and
 * `additional_job_slugs`. A schema that refused them made every successful write look failed.
 */
export const rcrmDealSchema = z.looseObject({
    id: z.number(),
    slug: z.string(),
    name: str,
    deal_stage: z.union([rcrmDealStageSchema, z.number(), z.string()]).nullish(),
    deal_value: numberish,
    /** An inconsistent UTC string that RCRM's UI shows in local time (Relay `export-no-co-deals.mjs`). */
    close_date: str,
    /** `{ id, label }` in production; a number in the docs. Never read. */
    deal_type: z.unknown().optional(),
    company_slug: str,
    /** An array of slugs in production; a comma-separated string in the docs. */
    contact_slugs: z.union([z.array(z.string()), z.string()]).nullish(),
    /** 0 or 1. */
    archived: numberish,
    custom_fields: customFields,
    ...audit,
});
/** `GET /v1/deals-pipeline`: the workspace's deal stages, `[{ id, label, percentage }]`. */
export const rcrmDealStagesResponseSchema = z.array(rcrmDealStageSchema);
/**
 * A note. Its `description` is HTML and RCRM refuses one over 10,000 characters. `GET` and the
 * update response both embed the related record whole as `related` (`Relay/.test/resp.txt`).
 */
export const rcrmNoteSchema = z.looseObject({
    id: z.number(),
    note_type: z.unknown().optional(),
    description: str,
    related_to: str,
    related_to_type: str,
    related: z.looseObject({}).nullish(),
    associated_candidates: z.array(z.unknown()).optional(),
    associated_companies: z.array(z.unknown()).optional(),
    associated_jobs: z.array(z.unknown()).optional(),
    associated_contacts: z.array(z.unknown()).optional(),
    associated_deals: z.array(z.unknown()).optional(),
    created_on: rcrmTimestampSchema.optional(),
    updated_on: rcrmTimestampSchema.optional(),
    created_by: rcrmUserRefSchema.nullish(),
    updated_by: rcrmUserRefSchema.nullish(),
});
export const rcrmCandidateListSchema = rcrmListSchema(rcrmCandidateSchema);
export const rcrmContactListSchema = rcrmListSchema(rcrmContactSchema);
export const rcrmCompanyListSchema = rcrmListSchema(rcrmCompanySchema);
export const rcrmJobListSchema = rcrmListSchema(rcrmJobSchema);
/**
 * `GET /v1/candidates/{slug}/hiring-stages`: an array of the candidate's job assignments, or —
 * with none — HTTP 200 with the PLAIN TEXT body `Not Assigned` (not JSON;
 * `Relay/apps/recruitment-portal/src/lib/pipeline-removal/run-pipeline-removal.ts`). The entry
 * shape was never recorded.
 */
export const rcrmHiringStagesSchema = z.array(z.looseObject({}));
export const RCRM_NOT_ASSIGNED = 'Not Assigned';
/** True for the `Not Assigned` body, bare or JSON-quoted (Relay accepts both). */
export function isRcrmNotAssigned(body) {
    const trimmed = body.trim();
    return trimmed === RCRM_NOT_ASSIGNED || trimmed === `"${RCRM_NOT_ASSIGNED}"`;
}
/**
 * `GET /v1/jobs-pipeline` — the workspace's job statuses, `[{ id, label }]`: documented as "Get Job
 * Stages" (https://docs.recruitcrm.io/docs/rcrm-api-reference/f385ecc4f48c2-get-job-stages). Relay
 * never called it; the ids and labels it recorded are the `job_status` a job carries.
 */
export const rcrmJobStatusesResponseSchema = z.array(rcrmJobStatusSchema);
//# sourceMappingURL=entities.js.map